import React, { useMemo } from 'react';
import loadable from '@loadable/component';
import Helmet from 'react-helmet';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { FullScreenLoader } from '../../components';
import { resolveAccessibleLayer } from '../../components/enums';
import { PRODUCTION_APP_ORIGIN } from '../../config';
import { GET_PUBLIC_SHARED_MAP_QUERY } from '../../gql';

const Scene = loadable(
  () => import('../../components/Scene').then((mod) => mod.Scene),
  { ssr: false }
);

interface PublicSharedMapProps {
  sharedMapLoading: boolean;
}

const PublicSharedMap = ({ sharedMapLoading }: PublicSharedMapProps) => {
  const theme = useTheme();
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const { shareSlug } = useParams();

  // Reuse the share query already loaded by useLoadShared; keep the shared
  // layer fixed so switching to base does not drop it from the picker.
  const { data } = useQuery(GET_PUBLIC_SHARED_MAP_QUERY, {
    variables: { slug: shareSlug },
    fetchPolicy: 'cache-only',
    skip: !shareSlug,
  });

  const allowedLayers = useMemo(() => {
    const sharedLayer =
      data?.getPublicSharedMap?.settings?.mapSettings?.layer ?? null;
    const accessibleLayer = resolveAccessibleLayer(sharedLayer, false);
    const keys = ['base'];

    if (accessibleLayer && accessibleLayer !== 'base') {
      keys.unshift(accessibleLayer);
    }

    return keys;
  }, [data]);

  return (
    <Stack direction="row" height="100%">
      <Helmet
        title="Shared map view | Acolyte"
        meta={[
          { property: 'og:title', content: 'Shared map view' },
          {
            property: 'og:description',
            content: 'Open this shared map in Acolyte.',
          },
          {
            property: 'og:image',
            content: `${PRODUCTION_APP_ORIGIN}/shared-map-preview.png`,
          },
          {
            property: 'og:url',
            content: `${PRODUCTION_APP_ORIGIN}/m/${shareSlug}`,
          },
          { name: 'twitter:card', content: 'summary_large_image' },
        ]}
      />
      {sharedMapLoading ? (
        <FullScreenLoader />
      ) : (
        <Scene
          isMobile={mobileBreakpoint}
          isPublic
          allowedLayers={allowedLayers}
        />
      )}
    </Stack>
  );
};

export { PublicSharedMap };
